// src/views/customer/pay-credit/CustomerPayCreditPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import validationErrors from '@/lib/validationErrors';
import ToastMe from '@/components/ui/ToastMe';

import {
  getCustomerCreditService,
} from '@/services/CustomerServices';

import {
  getTablePaymentService,
  getWaffiPaymentService,
  getEdahabPaymentService,
  getPremierPaymentService,
  getRaamsPaymentService,
  getRaamsGatewaysService,
  getRaamsPaymentVerifyService,
} from '@/services/PaymentService';

import { Input } from '@/components/ui/inputs/input';
import { InputSuffix } from '@/components/ui/inputs/input-suffix';

const CREDIT_GATEWAY_ID = '64b17c2f1e34a5d9bdf103a0';
const EXCLUDED_GATEWAY_SLUGS = new Set(['cash_on_delivery', 'cash-on-delivery', 'cod', 'cash']);
const RAAMS_ALLOWED_GATEWAY_IDS = new Set(['12', '13', '14', '16', '21']);

/* Mirror checkout behavior */
const sanitizeAccountNumber = (raw) => {
  let v = String(raw || '').replace(/\D+/g, '');
  if (v.startsWith('0')) v = v.slice(1);
  if (v.length > 9) v = v.slice(0, 9);
  return v;
};
const isNineDigits = (v) => /^\d{9}$/.test(String(v || ''));

const filterVisibleRaamsGateways = (list = []) => {
  const filtered = list.filter((gateway) =>
    RAAMS_ALLOWED_GATEWAY_IDS.has(String(gateway?.id)),
  );
  return filtered.length ? filtered : list;
};

const CustomerPayCreditPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const customerId = useSelector((s) => s.Auth.userData?._id);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: '',
      account_number: '',
      payment_method_id: '',
    },
    shouldFocusError: true,
    reValidateMode: 'onChange',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [credit, setCredit] = useState({ usedCredit: 0 });
  const [gateways, setGateways] = useState([]);
  const [gwId, setGwId] = useState(null);
  const [gwSlug, setGwSlug] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [raamsGateways, setRaamsGateways] = useState([]);
  const [raamsGatewayId, setRaamsGatewayId] = useState('');
  const [isRaamsGatewayLoading, setIsRaamsGatewayLoading] = useState(false);

  const outstanding = Number(credit.usedCredit) || 0;
  /* ── Credit summary ───────────────────────────────────── */
  useEffect(() => {
    if (!customerId) return;
    dispatch(getCustomerCreditService({ customerId }))
      .then((res) => setCredit(res?.data?.data || { usedCredit: 0 }))
      .catch(() => setCredit({ usedCredit: 0 }));
  }, [customerId, dispatch]);

  /* ── Prefill full outstanding ────────────────────────── */
  useEffect(() => {
    setValue('amount', outstanding.toFixed(2));
  }, [outstanding, setValue]);

  /* ── Payment gateways (exclude credit & COD-like) ────── */
  const loadGateways = useCallback(() => {
    setIsLoading(true);
    dispatch(getTablePaymentService())
      .then((res) => {
        const list =
          res?.data?.data?.filter(
            (g) => g._id !== CREDIT_GATEWAY_ID && !EXCLUDED_GATEWAY_SLUGS.has(g.slug),
          ) || [];
        setGateways(list);
        if (list.length) {
          setGwId(list[0]._id);
          setGwSlug(list[0].slug);
          setValue('payment_method_id', list[0]._id);
        }
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, setValue]);

  useEffect(loadGateways, [loadGateways]);

  const loadRaamsGateways = useCallback(() => {
    setIsRaamsGatewayLoading(true);
    dispatch(getRaamsGatewaysService())
      .then((res) => {
        const list = filterVisibleRaamsGateways(res?.data?.data || []);
        setRaamsGateways(list);
        if (list.length) {
          const firstId = String(list[0].id);
          setRaamsGatewayId((prev) => prev || firstId);
          setValue('raams_gateway_id', firstId);
        }
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsRaamsGatewayLoading(false));
  }, [dispatch, setValue]);

  useEffect(() => {
    if (gwSlug === 'raams' && !raamsGateways.length && !isRaamsGatewayLoading) {
      loadRaamsGateways();
    }
  }, [gwSlug, raamsGateways.length, isRaamsGatewayLoading, loadRaamsGateways]);

  /* ── Trigger payment ─────────────────────────────────── */
  const startGatewayPayment = (amount, accountNumberRaw = '') => {
    const clean = sanitizeAccountNumber(accountNumberRaw);
    const base = { customer_id: customerId, amount, account_number: clean };

    const thunk =
      gwSlug === 'raams'
        ? getRaamsPaymentService({ ...base, gateway_id: Number(raamsGatewayId) })
        : gwSlug === 'premier_wallets'
        ? getPremierPaymentService({ ...base, account_number: '00252' + clean })
        : gwSlug === 'edahab'
        ? getEdahabPaymentService(base)
        : getWaffiPaymentService(base);

    setSubmitting(true);
    dispatch(thunk)
      .then((res) => {
        if (gwSlug === 'raams' && res?.data?.data?.requiresRedirect && res?.data?.data?.redirect) {
          window.location.href = res.data.data.redirect;
          return;
        }

        if (gwSlug === 'raams' && res?.data?.data?.uuid) {
          ToastMe('Waiting for Raams confirmation…', 'success');
          const uuid = res.data.data.uuid;
          let attempts = 0;
          const timer = setInterval(() => {
            attempts += 1;
            dispatch(getRaamsPaymentVerifyService(uuid))
              .then((verifyRes) => {
                const data = verifyRes?.data?.data || {};
                if (data?.isPaid || data?.status === 'paid') {
                  clearInterval(timer);
                  ToastMe('Credit settled', 'success');
                  navigate('/customer/dashboard');
                } else if (data?.status === 'failed' || attempts >= 10) {
                  clearInterval(timer);
                }
              })
              .catch(() => {
                if (attempts >= 10) clearInterval(timer);
              });
          }, 3000);
          return;
        }

        ToastMe('Redirecting to payment…', 'success');
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setSubmitting(false));
  };

  /* ── Handlers ────────────────────────────────────────── */
  const handleGatewayChange = (gw) => {
    setGwId(gw._id);
    setGwSlug(gw.slug);
    setValue('payment_method_id', gw._id);
    // Clear phone when switching gateways (exactly like checkout)
    setValue('account_number', '');
    setRaamsGatewayId('');
    setValue('raams_gateway_id', '');
    if (gw.slug === 'raams') loadRaamsGateways();
  };

  const onInvalid = (formErrors) => {
    if (formErrors?.amount) {
      ToastMe(
        `Enter an amount between 0.01 and ${outstanding.toFixed(2)}`,
        'error',
      );
      return;
    }
    if (formErrors?.payment_method_id) {
      ToastMe('Select a payment method', 'error');
      return;
    }
    if (formErrors?.account_number) {
      ToastMe('Please enter a valid 9-digit mobile number (no leading 0)', 'error');
      return;
    }
    // Fallback (shouldn’t happen)
    ToastMe('Please correct the highlighted fields', 'error');
  };

  const onSubmit = ({ amount, account_number, payment_method_id }) => {
    if (!outstanding) {
      ToastMe('No outstanding credit', 'info');
      return;
    }
    if (!payment_method_id) {
      ToastMe('Select a payment method', 'error');
      return;
    }

    if (gwSlug === 'raams' && !raamsGatewayId) {
      ToastMe('Select a Raams gateway', 'error');
      return;
    }

    const payAmt = Number(amount);
    if (Number.isNaN(payAmt) || payAmt <= 0 || payAmt > outstanding) {
      ToastMe(`Enter an amount between 0.01 and ${outstanding.toFixed(2)}`, 'error');
      return;
    }

    // RHF already validated phone; this is a safety net
    const clean = sanitizeAccountNumber(account_number);
    if (!isNineDigits(clean)) {
      ToastMe('Please enter a valid 9-digit mobile number (no leading 0)', 'error');
      return;
    }

    startGatewayPayment(payAmt, account_number);
  };

  /* ── UI ──────────────────────────────────────────────── */
  return (
    <section className="pt-8 pb-16 min-h-[680px]">
      <div className="container max-w-[640px]">
        <h2 className="text-2xl font-semibold mb-6">Pay Outstanding Credit</h2>

        <div className="mb-6 p-4 bg-white shadow rounded-lg">
          <p className="text-gray-600">Outstanding Amount</p>
          <p className="text-3xl font-bold text-primary">${outstanding.toFixed(2)}</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          className="bg-white shadow rounded-lg p-6"
          noValidate
        >
          {/* Amount */}
          <Controller
            control={control}
            name="amount"
            rules={{
              required: true,
              min: 0.01,
              max: outstanding,
            }}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                step="0.01"
                labelText="Amount to Pay"
                size="sm"
                errorType={errors.amount?.type}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />

          {/* Payment methods */}
          <h3 className="font-medium mb-4 mt-6">Choose Payment Method</h3>
          <div className="payment-fieldset">
            {gateways.map((gw, idx) => (
              <label key={gw._id} htmlFor={`gw-${idx}`} className="payment-label">
                <Controller
                  control={control}
                  name="payment_method_id"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id={`gw-${idx}`}
                      type="radio"
                      value={gw._id}
                      checked={String(field.value || gwId) === String(gw._id)}
                      onChange={() => handleGatewayChange(gw)}
                    />
                  )}
                />
                <img
                  src={gw.icon_url || '/assets/images/payment-gateway/razorpay.png'}
                  alt={gw.name}
                />
                <span>{gw.name}</span>
              </label>
            ))}
          </div>

          {gwSlug === 'raams' && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Raams Gateway</h4>
              {isRaamsGatewayLoading ? (
                <p className="text-sm text-gray-500">Loading Raams gateways...</p>
              ) : (
                <div className="payment-fieldset">
                  {raamsGateways.map((gateway, idx) => (
                    <label key={gateway.id} htmlFor={`raams-credit-${idx}`} className="payment-label">
                      <Controller
                        control={control}
                        name="raams_gateway_id"
                        rules={{ required: gwSlug === 'raams' }}
                        render={({ field }) => (
                          <input
                            {...field}
                            id={`raams-credit-${idx}`}
                            type="radio"
                            value={gateway.id}
                            checked={String(field.value || raamsGatewayId) === String(gateway.id)}
                            onChange={() => {
                              setRaamsGatewayId(String(gateway.id));
                              setValue('raams_gateway_id', String(gateway.id));
                            }}
                          />
                        )}
                      />
                      <span>{gateway.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Phone number (same UX as checkout) */}
          {gwSlug && (
            <Controller
              control={control}
              name="account_number"
              rules={{
                required: true,
                validate: (v) => isNineDigits(sanitizeAccountNumber(v)) || '9 digits',
              }}
              render={({ field }) =>
                gwSlug === 'premier_wallets' ? (
                  <InputSuffix
                    {...field}
                    value={field.value || ''}
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    placeholder="Enter Mobile Number"
                    labelText="Mobile Number"
                    suffixText="00252"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={9}
                    onChange={(e) => {
                      const normalized = sanitizeAccountNumber(e.target.value);
                      field.onChange(normalized);
                      setValue('account_number', normalized);
                    }}
                    errorData={{ minLength: '9', maxLength: '9' }}
                    errorType={errors?.account_number?.type}
                  />
                ) : (
                  <Input
                    {...field}
                    value={field.value || ''}
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    placeholder="Enter Mobile Number"
                    labelText="Mobile Number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={9}
                    onChange={(e) => {
                      const normalized = sanitizeAccountNumber(e.target.value);
                      field.onChange(normalized);
                      setValue('account_number', normalized);
                    }}
                    errorData={{ minLength: '9', maxLength: '9' }}
                    errorType={errors?.account_number?.type}
                  />
                )
              }
            />
          )}

          {/* Guidance (like checkout) */}
          {gwSlug && (
            <span className="block text-xs text-gray-600 mt-2">
              Faldan numberka gali 0 ka tag <br />
              {gwSlug === 'edahab' ? (
                <>
                  0629018584 ❌ <br />
                  629018584 ✔️
                </>
              ) : (
                <>
                  0619018584 ❌ <br />
                  619018584 ✔️
                </>
              )}
            </span>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting || isLoading || !outstanding}
            aria-disabled={isSubmitting || isLoading || !outstanding}
            className="db-btn w-full mt-6 rounded-3xl capitalize font-medium leading-6 py-3 text-white bg-primary"
          >
            {isSubmitting ? (
              <>
                Processing…
                <svg
                  className="animate-spin -ml-1 ml-2 h-5 w-5 text-white inline-block align-[-2px]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12H4zm2
                      5.291A7.962 7.962 0 014 12H0c0 3.042
                      1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </>
            ) : (
              'Pay Now'
            )}
          </button>

          {/* Optional internal settle button (kept hidden/commented) */}
          {/* <button
            type="button"
            onClick={() => settleInternally(enteredAmount > 0 ? enteredAmount : outstanding)}
            className="w-full text-xs text-center text-gray-500 mt-3 underline"
          >
            Mark as paid manually (cash)
          </button> */}
        </form>
      </div>
    </section>
  );
};

export default CustomerPayCreditPage;
